import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { OGTags } from "@/components/OGTags";
import {
  QUIZ_QUESTIONS,
  QuizAnswers,
  answersToSearchParams,
} from "@/data/styleQuiz";

import { routeHead } from "@/lib/routeHead";

// Title/description come from scripts/routes.mjs, like the prerendered head.
const head = routeHead("/style-quiz");

const StyleQuiz = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({});

  const question = QUIZ_QUESTIONS[step];
  const total = QUIZ_QUESTIONS.length;

  const select = (value: string) => {
    const next: QuizAnswers = { ...answers, [question.id]: value };
    setAnswers(next);
    if (step === total - 1) {
      navigate(`/style-quiz/result?${answersToSearchParams(next)}`);
    } else {
      setStep(step + 1);
    }
  };

  const hasThumbnails = question.options.some((o) => o.image);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <OGTags
        title={head.title}
        description={head.description}
        url="https://loftloom.com/style-quiz"
        canonical="https://loftloom.com/style-quiz"
        image="https://loftloom.com/journal/28-hero.webp"
      />
      <Header />
      <Breadcrumbs items={[{ label: "Style Quiz" }]} />

      <main className="flex-1 w-[89%] max-w-[1140px] mx-auto pt-8 pb-16 md:pt-12 md:pb-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground font-semibold mb-2">
            Step {step + 1} of {total}
          </p>
          <h1 className="font-display text-2xl md:text-4xl tracking-[0.02em] text-foreground">
            {step === 0 ? "Style Quiz" : question.question}
          </h1>
          {step === 0 && (
            <p className="font-display text-lg md:text-2xl text-foreground/80 mt-2 tracking-[0.02em]">
              {question.question}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-3">
            Under a minute. We'll build a room board from pieces that are in stock right now.
          </p>
        </div>

        {/* Progress */}
        <div className="mt-6 md:mt-8 max-w-2xl mx-auto">
          <div
            className="h-[3px] w-full bg-border"
            role="progressbar"
            aria-valuenow={step + 1}
            aria-valuemin={1}
            aria-valuemax={total}
            aria-label="Quiz progress"
          >
            <div
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${((step + 1) / total) * 100}%` }}
            />
          </div>
        </div>

        <div
          className={`mt-8 md:mt-12 grid gap-3 md:gap-5 ${
            hasThumbnails
              ? "grid-cols-2 lg:grid-cols-4"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
          }`}
        >
          {question.options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => select(option.value)}
              aria-label={`${question.question} — ${option.label}`}
              className="group text-left border border-border bg-card hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-colors overflow-hidden"
            >
              {option.image && (
                <div className="overflow-hidden bg-muted aspect-[4/3]">
                  <img
                    src={option.image}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  />
                </div>
              )}
              <span className="flex items-center justify-between gap-2 px-4 py-3 md:py-4">
                <span className="font-display text-base md:text-lg tracking-[0.02em] text-foreground">
                  {option.label}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 shrink-0" />
              </span>
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between">
          {step > 0 ? (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-foreground hover:text-primary transition-colors"
              aria-label="Go back to the previous question"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
          ) : (
            <span />
          )}
          <Link
            to="/categories"
            className="text-[11px] tracking-[0.18em] uppercase font-bold text-muted-foreground hover:text-primary transition-colors"
          >
            Skip to all categories
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StyleQuiz;
